import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { EditorState, ContentState, Modifier } from 'draft-js'
import Editor from 'draft-js-plugins-editor'
import createMentionPlugin, { defaultSuggestionsFilter } from 'draft-js-mention-plugin'
import createHashtagPlugin from 'draft-js-hashtag-plugin';
import createLinkifyPlugin from 'draft-js-linkify-plugin';
import PostStorage from './PostStorage'

const MENTION_CONFIG = {
  entityMutability: 'IMMUTABLE',
  supportWhitespace: true,
  theme: {
    mention: 'text-tpnblue-800 bg-gray-200 rounded-md px-2 py-1',
    mentionSuggestions: 'mt-1 w-full rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm',
    mentionSuggestionsEntryFocused: 'text-white bg-tpnblue-50',
  },
}

const HASHTAG_CONFIG = {
  theme: {
    hashtag: 'text-tpnblue-800 bg-gray-200 rounded-md px-2 py-1',
  },
}

const LINK_CONFIG = {
  theme: {
    link: 'text-tpnblue-800',
  },
}

function MentionEntryAvatar({ mention }) {
  const { name, avatar } = mention

  if (avatar) {
    return (
      <img
        className="size-8 rounded-full" src={avatar} alt={name}
        title={name}
      />
    )
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400 size-8 rounded-full"
      viewBox="0 0 127.6 127.6"
      fill="currentColor"
    >
      <path className="st0" d="M0,0v127.6h127.6V0H0z M64.1,23c11.1,0.1,20.1,9.1,20.2,20.2c0,11.2-9.1,20.3-20.4,20.3 c-11.2,0-20.2-9.3-20.2-20.5C43.8,32,53,22.9,64.1,23z M102.9,103.1C92.1,114.6,79,120.2,64,120.6c-15.6-0.4-28.8-6.2-39.7-17.9 c-1.4-1.6-1.6-2.8-0.6-4.7c4.1-7.5,9.2-13.5,15.2-17.7c6.7-5.5,14.9-8.4,24.6-8.5c10.1-0.1,18.5,2.9,25.4,8.7 c5.8,4.1,10.7,9.9,14.8,17.2C104.9,99.9,104.6,101.3,102.9,103.1z" />
    </svg>
  )
}

MentionEntryAvatar.propTypes = {
  mention: PropTypes.shape({
    name: PropTypes.string,
    avatar: PropTypes.string,
  }),
}

function MentionEntry(props) {
  // eslint-disable-next-line no-unused-vars
  const { mention, theme, searchValue, isFocused, ...parentProps } = props

  return (
    <div {...parentProps}>
      <div className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900">
        <div className="flex items-center">
          <MentionEntryAvatar mention={mention} />
          <div className="block ml-2">
            <div className="font-medium text-gray-900">{mention.name}</div>
            {mention.type === 'USER' && <div className="text-gray-700">{mention.licensure}</div>}
            <div className="text-gray-500">{mention.location}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

MentionEntry.propTypes = {
  mention: PropTypes.object,
  theme: PropTypes.object,
  searchValue: PropTypes.any,
  isFocused: PropTypes.any,
}

class PostEditor extends Component {
  static propTypes = {
    id: PropTypes.string,
    class: PropTypes.string,
    value: PropTypes.string,
    name: PropTypes.string,
    data: PropTypes.shape({
      mentions: PropTypes.array,
    }),
    placeholder: PropTypes.string,
    phxLV: PropTypes.object,
    phxTarget: PropTypes.string,
  }

  constructor(props) {
    super(props)

    const { data = {}, placeholder = '', value } = props
    const { mentions = [] } = data

    this.editor = React.createRef()
    this.state = {
      editorState: EditorState.createWithContent(PostStorage.toJS(value || '', mentions || [])),
      placeholder,
      mentionUsers: [],
      postContent: '',
      hookMounted: false,
    }

    this.hashtagPlugin = createHashtagPlugin({
      ...HASHTAG_CONFIG,
    });

    this.MentionUser = createMentionPlugin({
      ...MENTION_CONFIG,
      mentionTrigger: '@',
    })

    this.linkifyPlugin = createLinkifyPlugin({
      ...LINK_CONFIG,
    })

    this.plugins = [this.MentionUser, this.hashtagPlugin, this.linkifyPlugin]
  }

  componentDidUpdate(_prevProps, _prevState) {
    const { phxLV } = this.props
    const { hookMounted } = this.state
    const { focus, reset, startMention, setPlaceholder } = this

    if (phxLV && !hookMounted) {
      this.setState({ hookMounted: true }, () => {
        phxLV.handleEvent('react:editor-focus', focus)
        phxLV.handleEvent('react:editor-reset', reset)
        phxLV.handleEvent('react:editor-start-mention', startMention)
        phxLV.handleEvent('react:editor-placeholder', setPlaceholder)
      })
    }
  }

  focus = ({ target }) => {
    if (target === this.props.id) {
      const element = document.querySelector(`#${target}`)

      this.editor.current.focus()
      window.scroll({ top: element.offsetTop - 250, behavior: 'smooth' })
    }
  }

  reset = ({ target }) => {
    if (target === this.props.id) {
      this.setState({
        postContent: '',
        editorState: EditorState.push(this.state.editorState, ContentState.createFromText(''), 'remove-range'),
      })
    }
  }

  setPlaceholder = ({ target, placeholder }) => {
    if (target === this.props.id) {
      this.setState({ placeholder })
    }
  }

  startMention = ({ target }) => {
    if (target === this.props.id) {
      const { editorState } = this.state
      const currentContent = editorState.getCurrentContent()
      const currentSelection = editorState.getSelection()
      const newTextObject = Modifier.replaceText(currentContent, currentSelection, '@')

      this.setState({
        editorState: EditorState.push(editorState, newTextObject, 'insert-characters'),
      })
      this.editor.current.focus()
    }
  }

  onEditorChange = (editorState) => {
    this.setState({
      editorState,
      postContent: PostStorage.toMD(editorState.getCurrentContent()),
    })
  }

  onSearchUsers = ({ value }) => {
    const payload = { target: this.props.id, value }

    if (this.props.phxTarget) {
      this.props.phxLV.pushEventTo(this.props.phxTarget, 'react:mention-suggestions', payload, (result, _ref) => {
        this.onSearchResults(value, result)
      })
    } else {
      this.props.phxLV.pushEvent('react:mention-suggestions', payload, (result, _ref) => {
        this.onSearchResults(value, result)
      })
    }
  }

  onSearchResults = (value, { target, data }) => {
    if (target === this.props.id) {
      this.setState({ mentionUsers: defaultSuggestionsFilter(value, data) })
    }
  }

  render() {
    const { editorState, postContent, mentionUsers, placeholder } = this.state
    const { name } = this.props
    const MentionUserSuggestions = this.MentionUser.MentionSuggestions

    return (
      <div className={this.props.class} onClick={() => this.editor.current.focus()}>
        <input type="hidden" value={postContent} name={name} />
        <Editor
          ref={this.editor}
          editorState={editorState}
          onChange={this.onEditorChange}
          plugins={this.plugins}
          placeholder={placeholder}
        />
        <MentionUserSuggestions
          onSearchChange={this.onSearchUsers}
          entryComponent={MentionEntry}
          suggestions={mentionUsers}
        />
      </div>
    )
  }
}

export default PostEditor
